import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  previousWindow,
  resolveWindow,
  type ScopeWindow,
} from '../../dashboard/utils/scopeWindow';
import type { Granularity } from '../utils/adminMetrics';

/* ============================================================
 * Admin scope — URL-backed, deep-linkable:
 * /admin/analytics?range=month&g=weekly&state=Tamil+Nadu&site=<id>
 *   &project=<id>&engineer=<id>&cmp=1
 * Drill hierarchy: platform (1) → state (2) → location (3) →
 * project (4) → engineer (5).
 * ============================================================ */

export type AdminPreset = 'today' | 'week' | 'month' | '30d' | 'custom';

export const ADMIN_PRESETS: Array<{ key: AdminPreset; label: string }> = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'This week' },
  { key: 'month', label: 'This month' },
  { key: '30d', label: 'Last 30 days' },
  { key: 'custom', label: 'Custom' },
];

const PRESET_KEYS: AdminPreset[] = ['today', 'week', 'month', '30d', 'custom'];
const GRANULARITY_KEYS: Granularity[] = ['daily', 'weekly', 'monthly'];
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

export type AdminLevel = 1 | 2 | 3 | 4 | 5;

export interface AdminScope {
  preset: AdminPreset;
  window: ScopeWindow;
  prevWindow: ScopeWindow;
  granularity: Granularity;
  /** Compare-periods mode: overlay the previous window as dashed ghosts. */
  compare: boolean;
  state: string | null;
  locationId: string | null;
  projectId: string | null;
  engineerId: string | null;
  level: AdminLevel;
}

export interface AdminScopeActions {
  setPreset: (preset: Exclude<AdminPreset, 'custom'>) => void;
  setCustomWindow: (window: ScopeWindow) => void;
  setGranularity: (g: Granularity) => void;
  toggleCompare: () => void;
  drillToState: (state: string) => void;
  drillToLocation: (locationId: string) => void;
  drillToProject: (projectId: string) => void;
  drillToEngineer: (engineerId: string) => void;
  setSelection: (sel: {
    state: string | null;
    locationId: string | null;
    projectId: string | null;
    engineerId: string | null;
  }) => void;
  drillUp: () => void;
  drillToLevel: (level: 1 | 2 | 3 | 4) => void;
}

function resolveAdminWindow(preset: AdminPreset, custom?: Partial<ScopeWindow>): ScopeWindow {
  if (preset === 'today') {
    const today = dayjs().format('YYYY-MM-DD');
    return { from: today, to: today };
  }
  return resolveWindow(preset, custom);
}

export function useAdminScope(): [AdminScope, AdminScopeActions] {
  const [params, setParams] = useSearchParams();

  const scope = useMemo<AdminScope>(() => {
    const rawPreset = params.get('range') ?? 'month';
    const preset = (PRESET_KEYS.includes(rawPreset as AdminPreset) ? rawPreset : 'month') as AdminPreset;
    const rawG = params.get('g') ?? 'weekly';
    const granularity = (GRANULARITY_KEYS.includes(rawG as Granularity) ? rawG : 'weekly') as Granularity;
    const from = params.get('from') ?? undefined;
    const to = params.get('to') ?? undefined;
    const custom =
      preset === 'custom' && from && DAY_RE.test(from) && to && DAY_RE.test(to) ? { from, to } : undefined;
    const effectivePreset = custom ? 'custom' : preset === 'custom' ? 'month' : preset;
    const window = resolveAdminWindow(effectivePreset, custom);
    const state = params.get('state');
    const locationId = params.get('site');
    const projectId = params.get('project');
    const engineerId = params.get('engineer');
    return {
      preset: effectivePreset,
      window,
      prevWindow: previousWindow(window),
      granularity,
      compare: params.get('cmp') === '1',
      state,
      locationId,
      projectId,
      engineerId,
      level: engineerId ? 5 : projectId ? 4 : locationId ? 3 : state ? 2 : 1,
    };
  }, [params]);

  const mutate = useCallback(
    (updates: Record<string, string | null>, options?: { replace?: boolean }) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(updates)) {
            if (value === null) next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: options?.replace ?? false }
      );
    },
    [setParams]
  );

  const actions = useMemo<AdminScopeActions>(
    () => ({
      setPreset: (preset) => mutate({ range: preset, from: null, to: null }, { replace: true }),
      setCustomWindow: (window) =>
        mutate({ range: 'custom', from: window.from, to: window.to }, { replace: true }),
      setGranularity: (g) => mutate({ g }, { replace: true }),
      toggleCompare: () => mutate({ cmp: scope.compare ? null : '1' }, { replace: true }),
      drillToState: (state) => mutate({ state, site: null, project: null, engineer: null }),
      drillToLocation: (locationId) => mutate({ site: locationId, project: null, engineer: null }),
      drillToProject: (projectId) => mutate({ project: projectId, engineer: null }),
      drillToEngineer: (engineerId) => mutate({ engineer: engineerId }),
      setSelection: (sel) =>
        mutate({ state: sel.state, site: sel.locationId, project: sel.projectId, engineer: sel.engineerId }),
      drillUp: () => {
        if (scope.engineerId) mutate({ engineer: null });
        else if (scope.projectId) mutate({ project: null });
        else if (scope.locationId) mutate({ site: null });
        else if (scope.state) mutate({ state: null });
      },
      drillToLevel: (level) => {
        if (level === 1) mutate({ state: null, site: null, project: null, engineer: null });
        else if (level === 2) mutate({ site: null, project: null, engineer: null });
        else if (level === 3) mutate({ project: null, engineer: null });
        else mutate({ engineer: null });
      },
    }),
    [mutate, scope.compare, scope.engineerId, scope.projectId, scope.locationId, scope.state]
  );

  return [scope, actions];
}
